/**
 * Service Worker policy: HARD KILL.
 *
 * Why: a previously-registered SW on production was serving stale bundles
 * to returning users (especially on iOS Safari, where `<meta http-equiv>`
 * cache hints are ignored and the SW lifecycle keeps the old version
 * "waiting" until every tab is closed). Incognito worked fine because
 * incognito has no SW state.
 *
 * Solution: stop registering any SW, and on every boot proactively
 * unregister any SW that may still be installed from previous deploys
 * AND wipe the Workbox/runtime caches it created. After one visit on the
 * new bundle, the user's device is clean and behaves like a normal SPA
 * (HTTP caching is handled by `public/_headers`, where /assets/* are
 * immutable-hashed and `/` + `/index.html` are `max-age=0, must-revalidate`).
 *
 * If we ever want to bring back PWA features, do it behind an explicit
 * opt-in flag — never auto-register on the production domain again until
 * we have a tested update story for iOS.
 */

const CACHE_PREFIXES_TO_DROP = ["workbox-", "arkeonx-"];

const dropAllAppCaches = async () => {
  if (typeof window === "undefined" || !("caches" in window)) return;
  try {
    const keys = await caches.keys();
    await Promise.all(
      keys
        .filter((k) => CACHE_PREFIXES_TO_DROP.some((p) => k.startsWith(p)))
        .map((k) => caches.delete(k).catch(() => false))
    );
  } catch {
    // ignore
  }
};

const unregisterAllServiceWorkers = async () => {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return false;
  try {
    const regs = await navigator.serviceWorker.getRegistrations();
    if (!regs.length) return false;
    await Promise.all(regs.map((r) => r.unregister().catch(() => false)));
    return true;
  } catch {
    return false;
  }
};

export const registerServiceWorker = async () => {
  if (typeof window === "undefined") return;
  if (!("serviceWorker" in navigator)) return;

  // Always: nuke any SW + caches left over from previous PWA deployments.
  const hadSw = await unregisterAllServiceWorkers();
  await dropAllAppCaches();

  // If we just unregistered a SW, the current page is still controlled
  // by it (controllers are sticky until navigation). Force a one-time
  // reload so the user immediately gets a non-SW-controlled session
  // serving the latest bundle. Use a sessionStorage flag so we don't
  // loop on browsers that fail to fully detach the controller.
  try {
    const FLAG = "arkeonx_sw_purge_reloaded";
    const alreadyReloaded = sessionStorage.getItem(FLAG) === "1";
    if (hadSw && navigator.serviceWorker.controller && !alreadyReloaded) {
      sessionStorage.setItem(FLAG, "1");
      window.location.reload();
    }
  } catch {
    // sessionStorage may be unavailable (private mode quirks) — ignore.
  }
};
